<template>
  <div class="absolute inset-0 flex size-full flex-col overflow-hidden">
    <CheckoutHeader v-if="withHeader" :redirect-route="baseUrlWithLocale" />

    <CheckoutProvider>
      <template
        #default="{
          addressList,
          billableCountries,
          items,
          summary,
          onUpdate,
          onUpdating,
          loading,
          shippableCountries,
          lastCollectionPoint,
          userInformation,
          isExpressCheckoutEligible,
        }"
      >
        <NuxtPage
          :address-list
          :billable-countries
          :discount="discountStore.discount"
          :discount-token="discountStore.token"
          :is-express-checkout-eligible
          :items
          :last-collection-point
          :loading
          :payment-method="discountStore.paymentMethod"
          :shippable-countries
          :summary
          :swap-offer="swapStore.offer"
          :user-information
          @update="onUpdate"
          @updating="onUpdating"
          @vue:before-mount="removeInStoreMobilePlan(items, onUpdate)"
        />
      </template>
    </CheckoutProvider>

    <div id="toast-wrapper" class="fixed z-50">
      <TheToast to="#toast-wrapper" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { navigateTo, useHead } from '#imports'
import { computed, onMounted } from 'vue'

import {
  FraudDetectionStep,
  useFraudDetectionScripts,
} from '@backmarket/nuxt-layer-payment/fraud/composables/useFraudDetectionScripts'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useMarketBaseUrl } from '@backmarket/nuxt-module-marketplace/useMarketBaseUrl'
import TheToast from '@backmarket/nuxt-module-toast/TheToast.vue'

import type { CartItem } from '@backmarket/nuxt-layer-checkout/api/cart/types'
import CheckoutHeader from '@backmarket/nuxt-layer-checkout/components/CheckoutHeader/CheckoutHeader.vue'
import CheckoutProvider from '@backmarket/nuxt-layer-checkout/components/CheckoutProvider/CheckoutProvider.vue'
import { useDiscountStore } from '@backmarket/nuxt-layer-checkout/stores/discountStore'
import { useSwapStore } from '@backmarket/nuxt-layer-checkout/stores/swapStore'
import { isBouyguesOffer } from '@backmarket/nuxt-layer-checkout/utils/mobilePlan/isBouyguesOffer'

import translations from './CheckoutFlow.translations'

defineProps<{
  withHeader?: boolean
}>()

const swapStore = useSwapStore()
const discountStore = useDiscountStore()
const $httpFetch = useHttpFetch()
const i18n = useI18n()
const currentLocale = useI18nLocale()
const { baseUrlWithLocale } = useMarketBaseUrl('main')

useHead({
  title: i18n(translations.title),
  htmlAttrs: { lang: currentLocale },
  meta: [
    {
      name: 'description',
      content: i18n(translations.description),
    },
  ],
})

useFraudDetectionScripts({
  currentStep: computed((): FraudDetectionStep => 'CHECKOUT_IN_PROGRESS'),
})

async function removeInStoreMobilePlan(items: CartItem[], update: () => void) {
  const itemWithSelectedMobilePlan = items.find((item) => {
    if (!item.mobilePlan) return false

    return isBouyguesOffer(item.mobilePlan.selectedOffer)
  })

  if (!itemWithSelectedMobilePlan || !itemWithSelectedMobilePlan.mobilePlan)
    return

  if (itemWithSelectedMobilePlan.mobilePlan.selectedOffer.type === 'IN_STORE') {
    await $httpFetch('/bm/cart/update_quantity', {
      method: 'POST',
      body: {
        action: 'delete',
        listing_id: itemWithSelectedMobilePlan.listingId,
        new_quantity: itemWithSelectedMobilePlan.quantity,
        listing_price: itemWithSelectedMobilePlan.price,
      },
    })

    update()

    await navigateTo(
      `${baseUrlWithLocale}/checkout/in-store/bouygues-telecom/error`,
    )
  }
}

onMounted(() => {
  sessionStorage.setItem('showCatchupModal', 'true')
})
</script>
